import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { GraphNode } from 'features/knowledgeDocumentViewer/graph/graphTypes'
import { loadOffPageConnectorPageDestination } from 'features/knowledgeDocumentViewer/graph/offPageConnectorPageDestination'
import { useGeckoNotifications } from 'features/notifications'
import { postFetcher } from 'features/rolodex/fetchers'
import { useCallback, useLayoutEffect, useRef } from 'react'

import { useIsGraphModeEnabled } from './useIsGraphModeEnabled'

export type OpenOffPageConnector = (
  peer: GraphNode,
  openInNewTab: boolean
) => void

export function useOpenOffPageConnector(
  documentId: string,
  pageNumber: number,
  isActiveTab: boolean
): OpenOffPageConnector {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { orgID, siteID } = useKnowledgeDocumentDataContext()
  const {
    activeTabId,
    isLabelerMode,
    isEvalSessionLocked,
    pushToHistory,
    addDocumentTab,
  } = useKnowledgeDocumentUXContext()
  const graphMode = useIsGraphModeEnabled()
  const { selectedPipelineConfigId, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const { errorNotification } = useGeckoNotifications()
  const enabled =
    isActiveTab &&
    graphMode &&
    !isLabelerMode &&
    !isEvalSessionLocked &&
    isAuthenticated &&
    !isPipelineConfigScopeLoading
  const requestCounter = useRef({ version: 0 }).current
  useLayoutEffect(() => {
    requestCounter.version++
    return (): void => {
      requestCounter.version++
    }
  }, [
    requestCounter,
    documentId,
    pageNumber,
    activeTabId,
    enabled,
    orgID,
    siteID,
    selectedPipelineConfigId,
  ])
  return useCallback(
    (peer, openInNewTab): void => {
      if (
        !enabled ||
        peer.tags.organization !== orgID ||
        peer.tags.site !== siteID ||
        peer.tags['pipeline-config'] !== selectedPipelineConfigId
      )
        return
      const currentRequestVersion = ++requestCounter.version
      void loadOffPageConnectorPageDestination(peer, (query) =>
        postFetcher(
          `${ROLODEX_URL}/api/v2/entries/load`,
          JSON.stringify(query),
          authorizedAxios
        )
      )
        .then((destination): void => {
          if (currentRequestVersion !== requestCounter.version) return
          if (openInNewTab)
            addDocumentTab({ ...destination, selectedBoxIds: [] })
          else
            pushToHistory(activeTabId, {
              type: 'document',
              ...destination,
              selectedBoxIds: [],
              query: '',
            })
        })
        .catch((): void => {
          if (currentRequestVersion === requestCounter.version)
            errorNotification(
              t`Could not open the connected page. Please try again.`
            )
        })
    },
    [
      enabled,
      orgID,
      siteID,
      selectedPipelineConfigId,
      requestCounter,
      ROLODEX_URL,
      authorizedAxios,
      addDocumentTab,
      pushToHistory,
      activeTabId,
      errorNotification,
    ]
  )
}
